/**
 * ErrorBoundary catches errors in the app and renders a fallback UI
 * Reference from NextJS docs:
 * https://nextjs.org/docs/14/pages/building-your-application/configuring/error-handling#handling-client-errors
 */
import { Env } from '@/common/types';
import React from 'react';
import { FallbackPage } from '../Fallback';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  caller: string;
  env: Env;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // TODO: Add error logging to monitoring service
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error', { error, errorInfo, caller: this.props.caller });
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return <FallbackPage env={this.props.env} statusCode={500} />;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
